import { useEffect, useState } from 'react';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { ClientsCardGrid } from '@/components/clients/ClientCard';
import { ClientsTable } from '@/components/clients/ClientsTable';
import { clientsService, Client } from '@/services/clients.service';
import { getMultipleClientAnalytics } from '@/services/analytics.service';
import { ClientAnalytics } from '@/types';
import { Link } from 'react-router-dom';
import { auth } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ViewToggle, useViewPreference } from '@/components/ui/view-toggle';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useToast } from '@/components/ui/Toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Users,
  Search,
} from 'lucide-react';

type ClientType = 'all' | 'business' | 'private';

function ClientsContent() {
  const [clients, setClients] = useState<Client[]>([]);
  const [clientAnalytics, setClientAnalytics] = useState<
    Map<string, ClientAnalytics>
  >(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useViewPreference('clients', 'table');
  const [typeFilter, setTypeFilter] = useState<ClientType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteClient, setDeleteClient] = useState<Client | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const user = auth.currentUser;
      if (!user) {
        throw new Error('Not authenticated');
      }
      const token = await user.getIdToken();

      const clientsData = await clientsService.getClients();
      setClients(clientsData);

      if (clientsData.length > 0) {
        const clientIds = clientsData.map((c) => c.id);
        const clientAnalyticsData = await getMultipleClientAnalytics(
          clientIds,
          token
        );
        setClientAnalytics(clientAnalyticsData);
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load data';
      console.error('Error loading data:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!deleteClient) return;
    try {
      await clientsService.deleteClient(deleteClient.id);
      setClients((prev) => prev.filter((c) => c.id !== deleteClient.id));
      toast.success('Client deleted', 'The client has been removed.');
    } catch (err) {
      toast.error('Error', 'Failed to delete client.');
    } finally {
      setDeleteClient(null);
    }
  };

  // Filter clients
  const filteredClients = clients.filter((client) => {
    if (typeFilter !== 'all' && client.type !== typeFilter) return false;
    if (searchQuery && !client.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  // Merge clients with their analytics
  const clientsWithStats = filteredClients.map((client) => {
    const clientStats = clientAnalytics.get(client.id);
    return {
      ...client,
      totalHours: clientStats?.totalHours || 0,
      earnings: clientStats?.totalEarnings || 0,
      activeTasks: clientStats?.activeTasks || 0,
    };
  });

  const emptyState = (
    <div className="text-center py-12 border border-border rounded-lg">
      <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
      <h3 className="text-lg font-semibold text-foreground mb-2">
        No clients yet
      </h3>
      <p className="text-muted-foreground mb-4">
        Get started by adding your first client
      </p>
      <Button asChild>
        <Link to="/clients/new">
          <Plus className="w-4 h-4 mr-2" />
          Add Your First Client
        </Link>
      </Button>
    </div>
  );

  return (
    <AppLayout>
      <Breadcrumb items={[{ label: 'Clients' }]} />

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-96" />
        </div>
      ) : (
        <>
          {/* Toolbar - Inline search and filters */}
          <div className="flex items-center gap-4 mb-6">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Type Filter */}
            <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as ClientType)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="business">Business</SelectItem>
                <SelectItem value="private">Private</SelectItem>
              </SelectContent>
            </Select>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Add Button */}
            <Button asChild>
              <Link to="/clients/new">
                <Plus className="w-4 h-4" />
              </Link>
            </Button>

            {/* View Toggle */}
            <ViewToggle view={view} onViewChange={setView} onRefresh={loadData} />
          </div>

          {/* Content */}
          {view === 'table' ? (
            <ClientsTable
              clients={clientsWithStats}
              onDelete={setDeleteClient}
              emptyState={emptyState}
            />
          ) : (
            <ClientsCardGrid clients={clientsWithStats} emptyState={emptyState} />
          )}
        </>
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deleteClient}
        onClose={() => setDeleteClient(null)}
        onConfirm={handleDeleteClient}
        title="Delete Client"
        description={`Are you sure you want to delete "${deleteClient?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
      />
    </AppLayout>
  );
}

export default function ClientsPage() {
  return (
    <ProtectedRoute>
      <ClientsContent />
    </ProtectedRoute>
  );
}
